import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { projects } from '../../../mocks/references';
import BeforeAfterSlider from '../../../components/base/BeforeAfterSlider';

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return { ref, inView };
}

const featured = projects.slice(0, 4);

export default function ReferencieSection() {
  const { ref, inView } = useInView();

  return (
    <section id="referencie" className="py-28 bg-zinc-950">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div
          ref={ref}
          className={`text-center mb-16 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="inline-flex items-center gap-2 bg-zinc-800/60 border border-zinc-700/40 rounded-full px-4 py-1.5 mb-6">
            <i className="ri-star-fill text-orange-400 text-sm" />
            <span className="text-zinc-300 text-sm font-semibold">Referencie</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4 leading-tight">
            Toto je to, čo{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-400">
              predáva za vás
            </span>
          </h2>
          <p className="text-zinc-400 text-lg max-w-xl mx-auto">
            Od nuly až po kompletný redesign - konkrétne projekty, konkrétne čísla.
          </p>

          {/* Type legend */}
          <div className="flex items-center justify-center gap-4 mt-6">
            <span className="inline-flex items-center gap-1.5 text-xs text-zinc-400">
              <span className="w-2 h-2 rounded-full bg-orange-400" />
              Weby & E-shopy
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs text-zinc-400">
              <span className="w-2 h-2 rounded-full bg-teal-400" />
              CRM & Systémy
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs text-zinc-400">
              <span className="w-2 h-2 rounded-full bg-violet-400" />
              Osobný brand
            </span>
          </div>
        </div>

        {/* Projects grid */}
        <div
          className={`grid grid-cols-1 md:grid-cols-2 gap-6 mb-10 transition-all duration-700 delay-200 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {featured.map((project) => (
            <div
              key={project.id}
              className="group relative rounded-2xl overflow-hidden border border-zinc-800/60 hover:border-orange-500/30 transition-all duration-300 bg-zinc-800"
            >
              {/* Browser mockup */}
              <div className="p-3 pt-0">
                <div className="bg-zinc-700 rounded-t-lg px-3 py-2 flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-400/80" />
                    <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/80" />
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/80" />
                  </div>
                  <div className="flex-1 bg-zinc-600 rounded px-2 py-0.5 text-zinc-400 text-[10px] truncate">
                    {project.liveUrl ?? 'boostio.sk'}
                  </div>
                </div>
                <div className="aspect-video overflow-hidden rounded-b-lg relative bg-zinc-900">
                  {project.type === 'redesign' && project.beforeImage && project.afterImage ? (
                    <BeforeAfterSlider
                      beforeImage={project.beforeImage}
                      afterImage={project.afterImage}
                      beforeLabel="Pred"
                      afterLabel="Po"
                      className="w-full h-full"
                    />
                  ) : (
                    <img
                      src={project.image}
                      alt={project.name}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  )}
                </div>
              </div>

              {/* Info */}
              <div className="px-4 pb-4 pt-1">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  {project.type === 'redesign' ? (
                    <span className="text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/25 rounded-full px-2.5 py-0.5 font-semibold flex items-center gap-1">
                      <i className="ri-refresh-line text-xs" />Redesign
                    </span>
                  ) : (
                    <span className="text-xs text-orange-400 bg-orange-500/10 border border-orange-500/25 rounded-full px-2.5 py-0.5 font-semibold flex items-center gap-1">
                      <i className="ri-rocket-2-line text-xs" />Od nuly
                    </span>
                  )}
                  <span className="text-xs text-zinc-400 bg-zinc-800/80 rounded-full px-2.5 py-0.5">{project.category}</span>
                  {project.caseStudyLink && (
                    <span className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-full px-2.5 py-0.5 font-bold flex items-center gap-1">
                      <i className="ri-trophy-line text-xs" />Case Study
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <h3 className="text-white font-bold text-base">{project.name}</h3>
                  <span className="text-orange-400 text-xs font-bold whitespace-nowrap">{project.result}</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap mt-1.5">
                  {project.tags.slice(0, 3).map((tag) => (
                    <span key={tag} className="text-xs text-zinc-500 bg-zinc-800/80 rounded px-2 py-0.5">{tag}</span>
                  ))}
                  {project.caseStudyLink && (
                    <Link
                      to={project.caseStudyLink}
                      className="ml-auto inline-flex items-center gap-1 bg-orange-500 hover:bg-orange-400 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-all duration-200 whitespace-nowrap"
                    >
                      Čítať case study <i className="ri-arrow-right-line" />
                    </Link>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA to full page */}
        <div
          className={`text-center transition-all duration-700 delay-300 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <Link
            to="/referencie"
            className="inline-flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700/60 hover:border-zinc-600 text-white text-sm font-semibold px-7 py-3.5 rounded-xl transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            Zobraziť všetky referencie
            <i className="ri-arrow-right-line" />
          </Link>
          <p className="text-zinc-600 text-xs mt-3">12 reálnych projektov · weby, systémy, osobné branding</p>
        </div>
      </div>
    </section>
  );
}
